import { useMemo } from 'react'
import { useCruiseMemberPricing } from 'hooks/Cruise/useCruiseMemberPricing'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import {
  getCruiseLowestPriceSelection,
  getCruiseLowestBundlePriceSelection,
  getCruiseMemberPriceByCategory,
} from 'lib/cruises/cruiseOfferPricing'
import {
  getCruiseDiscountPills,
  getCruiseOfferMemberPriceVariation,
  getCruiseOfferPriceVariation,
  type CruiseOfferPriceVariation,
} from 'lib/cruises/cruiseUtils'

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  cabinTypes?: string
  filters?: App.OfferListFilters
  sortBy?: string
  cruisePriceByNight: boolean
}

export default function useCruiseOfferPricing({
  offer,
  cabinTypes,
  filters,
  sortBy,
  cruisePriceByNight,
}: Props) {
  const isCurrentCruiseOffer = isCruiseOffer(offer)
  const mainDepartureDetails = 'mainDepartureDetails' in offer ? offer.mainDepartureDetails : undefined
  const shouldUseLuxPlusPriceSelection = !!(
    isCurrentCruiseOffer &&
    filters?.offerTypes?.includes('cruise') &&
    (filters.hasLuxPlusPrice || filters.isLuxPlus)
  )
  const { departureMemberPricing, memberPricingLoading, canSeeLuxPlusLabel, canSeeLuxPlusPrice } = useCruiseMemberPricing(
    isCurrentCruiseOffer ? offer : undefined,
    mainDepartureDetails,
  )

  const lowestPriceSelection = useMemo(() => {
    if (!isCurrentCruiseOffer) return undefined

    return getCruiseLowestPriceSelection(offer, {
      cabinTypes,
      minPrice: filters?.minPrice,
      maxPrice: filters?.maxPrice,
      sortBy,
      cruisePriceByNight,
      shouldUseLuxPlusPriceSelection,
      departureMemberPricing,
    })
  }, [
    offer,
    isCurrentCruiseOffer,
    cabinTypes,
    filters?.minPrice,
    filters?.maxPrice,
    sortBy,
    cruisePriceByNight,
    shouldUseLuxPlusPriceSelection,
    departureMemberPricing,
  ])

  const lowestBundlePriceSelection = useMemo(() => {
    if (!isCurrentCruiseOffer) return undefined

    return getCruiseLowestBundlePriceSelection(offer, {
      cabinTypes,
      minPrice: filters?.minPrice,
      maxPrice: filters?.maxPrice,
      sortBy,
      cruisePriceByNight,
    })
  }, [offer, isCurrentCruiseOffer, cabinTypes, filters?.minPrice, filters?.maxPrice, sortBy, cruisePriceByNight])

  const lowestBundlePrice = useMemo(() => {
    if (!isCurrentCruiseOffer || !lowestBundlePriceSelection) return undefined

    return getCruiseOfferPriceVariation(offer, lowestBundlePriceSelection.price, cruisePriceByNight)
  }, [offer, isCurrentCruiseOffer, lowestBundlePriceSelection, cruisePriceByNight])

  const lowestPrice = useMemo((): CruiseOfferPriceVariation => {
    if (isCurrentCruiseOffer) {
      return getCruiseOfferPriceVariation(offer, lowestPriceSelection?.price, cruisePriceByNight)
    }

    const saleUnit = cruisePriceByNight ? 'night' : offer.saleUnit.toLowerCase() === 'cruise' ? 'person' : offer.saleUnit
    const lowestPrice = offer.lowestPricePackage?.price || 0
    const total = cruisePriceByNight ? Math.round(lowestPrice / offer.minDuration! || 1) : lowestPrice
    return {
      primaryPrice: {
        total,
        type: saleUnit,
      },
    }
  }, [cruisePriceByNight, isCurrentCruiseOffer, lowestPriceSelection?.price, offer])

  const memberLowestPrice = useMemo(() => {
    if (!isCurrentCruiseOffer) {
      return undefined
    }

    const departureMemberPricingByCategory = lowestPriceSelection?.departureId
      ? departureMemberPricing
        ?.find((d) => d.id === lowestPriceSelection.departureId)
        ?.lowestPricesByCategory
      : undefined

    const memberPriceDetails = getCruiseMemberPriceByCategory(
      departureMemberPricingByCategory,
      lowestPriceSelection?.price?.cabinCategory,
    )
    const memberPrice = lowestPriceSelection?.memberPrice ?? memberPriceDetails?.memberPrice

    return getCruiseOfferMemberPriceVariation(offer, memberPrice, cruisePriceByNight)
  }, [
    departureMemberPricing,
    offer,
    cruisePriceByNight,
    lowestPriceSelection?.departureId,
    lowestPriceSelection?.memberPrice,
    lowestPriceSelection?.price?.cabinCategory,
    isCurrentCruiseOffer,
  ])

  const memberPriceType = useMemo(() => {
    if (!isCurrentCruiseOffer) return undefined
    if (lowestPriceSelection?.memberPriceType) return lowestPriceSelection.memberPriceType

    return getCruiseMemberPriceByCategory(
      departureMemberPricing
        ?.find((departure) => departure.id === lowestPriceSelection?.departureId)
        ?.lowestPricesByCategory,
      lowestPriceSelection?.price?.cabinCategory,
    )?.memberPriceType
  }, [departureMemberPricing, isCurrentCruiseOffer, lowestPriceSelection])

  const discountPills = useMemo(
    () => getCruiseDiscountPills(lowestPriceSelection?.price),
    [lowestPriceSelection?.price],
  )

  const bundleDiscountPills = useMemo(
    () => getCruiseDiscountPills(lowestBundlePriceSelection?.price),
    [lowestBundlePriceSelection?.price],
  )

  return {
    discountPills,
    bundleDiscountPills,
    lowestPrice,
    lowestBundlePrice,
    memberLowestPrice,
    memberPriceType,
    memberPricingLoading,
    shouldUseLuxPlusPriceSelection,
    canSeeLuxPlusLabel,
    canSeeLuxPlusPrice,
  }
}
